import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Box, X, LayoutTemplate, Crown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL } from '@/utils/apiUrl';


// Templates Modal — extracted from DesignStudio.jsx (Feb 2026 refactor)
export default function TemplatesModal({ isOpen, onClose, onSelectTemplate, language }) {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    fetch(`${API_URL}/api/templates?language=${language}`)
      .then(r => r.ok ? r.json() : { templates: [] })
      .then(d => setTemplates(d.templates || []))
      .catch(err => console.error('Templates fetch error:', err))
      .finally(() => setIsLoading(false));
  }, [isOpen, language]);

  const categories = [
    { id: 'all', name: language === 'tr' ? 'Tümü' : 'All' },
    { id: 'furniture', name: language === 'tr' ? 'Mobilya' : 'Furniture' },
    { id: 'robotics', name: language === 'tr' ? 'Robotik' : 'Robotics' },
    { id: 'electronics', name: language === 'tr' ? 'Elektronik' : 'Electronics' },
    { id: 'aerospace', name: language === 'tr' ? 'Havacılık' : 'Aerospace' },
    { id: 'wearables', name: language === 'tr' ? 'Giyilebilir' : 'Wearables' },
    { id: 'automotive', name: language === 'tr' ? 'Otomotiv' : 'Automotive' },
  ];

  const filteredTemplates = selectedCategory === 'all'
    ? templates
    : templates.filter(t => t.category === selectedCategory);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-blue-500/30 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-[#0A0A0A]">
          <div className="flex items-center gap-3">
            <LayoutTemplate className="w-6 h-6 text-blue-400" />
            <div>
              <h3 className="text-lg font-bold text-white">
                {language === 'tr' ? 'Tasarım Şablonları' : 'Design Templates'}
              </h3>
              <p className="text-sm text-slate-400">
                {language === 'tr' ? 'Hazır şablonlarla hızlı başlayın' : 'Start quickly with ready templates'}
              </p>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex gap-2 p-4 border-b border-slate-800 overflow-x-auto">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 180px)' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map(template => (
                <motion.div
                  key={template.template_id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden hover:border-blue-500/50 transition-all cursor-pointer group"
                  onClick={() => { onSelectTemplate(template); onClose(); }}
                >
                  <div className="h-32 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center relative">
                    <Box className="w-12 h-12 text-slate-600 group-hover:text-blue-400 transition-colors" />
                    {template.is_premium && (
                      <div className="absolute top-2 right-2 px-2 py-1 bg-amber-500/90 text-white text-xs rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Premium
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-white mb-1">{template.name}</h4>
                    <p className="text-sm text-slate-400 line-clamp-2">{template.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-slate-500 capitalize">{template.category}</span>
                      <span className="text-xs text-blue-400">{template.usage_count} {language === 'tr' ? 'kullanım' : 'uses'}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
